import React, { useState } from "react";
import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { api } from "./api";
import { useDraft } from "./ApplicationSteps";
import { ErrorBanner, OptionTile, PreviewLock, StepActions, StepCard, SubHeading } from "./formParts";
import { T } from "./signupDesign";

export default function TermsStep({ view, meta, token, preview, onSaved, onNext, onBack, registerDraft }) {
  const [consents, setConsents] = useState(() => ({ ...((view.application.terms && view.application.terms.consents) || {}) }));
  const [firstConsents] = useState(() => JSON.stringify(consents));
  // Moving to another step keeps what was checked (only the owner sees this step).
  useDraft(registerDraft, JSON.stringify(consents) !== firstConsents, () => api("PUT", "/application/terms", { token, body: { consents } }).then(onSaved));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const legalName = view.application.legal.legalName || "The Merchant";

  const next = async () => {
    if (preview) return onNext();
    setBusy(true);
    setError(null);
    try {
      const res = await api("PUT", "/application/terms", { token, body: { consents } });
      onSaved(res);
      setErrors(res.errors || {});
      if (Object.keys(res.errors || {}).length) setError("Please accept each required item to continue.");
      else onNext();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard title="Fees and authorizations" subtitle="Review the fees, then accept the processor's application terms. You'll sign all of this together on the next step.">
      <ErrorBanner error={error} />
      <SubHeading first>RushMyFood fees (Schedule A)</SubHeading>
      <Box sx={{ overflowX: "auto", mb: 3, border: `1px solid ${T.cardBorder}`, borderRadius: "16px" }}>
        <Table size="small" sx={{ minWidth: 480, "& td": { verticalAlign: "top", py: 1.5, px: 2 }, "& tr:last-child td": { borderBottom: 0 } }}>
          <TableBody>
            {/* This restaurant's own fees if RushMyFood set them, else the standard (backend utils/agreementFees.js). */}
            {((view.fees && view.fees.scheduleA) || meta.scheduleA || []).map(({ item, amount, description }) => (
              <TableRow key={item}>
                <TableCell sx={{ fontWeight: 600, width: "32%", color: T.text }}>{item}</TableCell>
                <TableCell sx={{ width: "22%", whiteSpace: "nowrap", color: T.text, fontWeight: 600 }}>
                  {amount.map((line) => <Box key={line}>{line}</Box>)}
                </TableCell>
                <TableCell sx={{ color: T.text2 }}>{description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <SubHeading>Payment processor fee disclosure</SubHeading>
      <Box sx={{ border: `1px solid ${T.cardBorder}`, background: T.footerBg, borderRadius: "16px", p: 2, mb: 3, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 4, rowGap: 0.75 }}>
        {((view.fees && view.fees.processorFees) || meta.processorFees).map(([label, amount]) => (
          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, fontSize: 14, color: T.text2, fontVariantNumeric: "tabular-nums" }}>
            <span>{label}</span>
            <strong style={{ color: T.text }}>{amount}</strong>
          </Box>
        ))}
      </Box>

      <SubHeading>Authorizations</SubHeading>
      <PreviewLock on={preview}>
      {meta.consents.map((c) => (
        <Box key={c.key} sx={{ mb: 2, p: { xs: 2, sm: 2.5 }, borderRadius: "16px", background: T.card, border: `1px solid ${errors[`consents.${c.key}`] ? T.error : T.cardBorder}` }}>
          <Typography component="h4" sx={{ fontWeight: 700, fontSize: 16, color: T.text, mb: 0.75 }}>
            {c.title}
            {!c.required && <Box component="span" sx={{ fontWeight: 400, color: T.muted }}> (optional)</Box>}
          </Typography>
          <Typography sx={{ color: T.text2, fontSize: 15, lineHeight: 1.65, mb: 1.5 }}>{c.text.replace("{legalName}", legalName)}</Typography>
          <OptionTile
            checked={!!consents[c.key]}
            onChange={(e) => setConsents((x) => ({ ...x, [c.key]: e.target.checked }))}
            label="I agree"
            error={!!errors[`consents.${c.key}`]}
            sx={{ py: 1.25, display: "inline-flex", pr: 3 }}
          />
        </Box>
      ))}
      </PreviewLock>
      <StepActions onBack={onBack} onNext={next} busy={busy} />
    </StepCard>
  );
}
